export type DisplayPoint = { x: number; y: number };
export type DisplayFootprint = {
  x: number; y: number; halfWidth: number; halfHeight: number; circle?: boolean;
};
export type DisplayCollisionItem = {
  id: string;
  position: DisplayPoint;
  origin: DisplayPoint;
  formulaPosition: DisplayPoint;
  /** Resolved physical mounting height in metres. */
  z?: number;
  /** Optional wall normal, overriding the radial displacement direction. */
  direction?: DisplayPoint;
  wallDocked?: boolean;
  movable: boolean;
  footprints: readonly DisplayFootprint[];
};

/** Wall tangent rotation follows the owner's geometry, including oblique walls.
 * The side of the mounting point selects the normal, so opening devices on
 * either side move away from the wall. Points on the wall use its inward normal.
 */
export function eloWallCollisionDirection(position: DisplayPoint, origin: DisplayPoint, rotation: number): DisplayPoint {
  const radians = rotation * Math.PI / 180;
  const normal = { x: -Math.sin(radians), y: Math.cos(radians) };
  const offset = (position.x - origin.x) * normal.x + (position.y - origin.y) * normal.y;
  const sign = offset < -1e-10 ? -1 : 1;
  return { x: normal.x * sign, y: normal.y * sign };
}

/** Open overlap interval along the outward ray; touching boundaries are allowed. */
function overlapInterval(a: DisplayFootprint, b: DisplayFootprint, direction: DisplayPoint, gap: number): [number, number] | undefined {
  const dx = a.x - b.x, dy = a.y - b.y;
  if (a.circle && b.circle) {
    const radius = a.halfWidth + b.halfWidth + gap;
    const projection = dx * direction.x + dy * direction.y;
    const discriminant = projection * projection - (dx * dx + dy * dy - radius * radius);
    if (discriminant <= 0) return;
    const root = Math.sqrt(discriminant);
    return [-projection - root, -projection + root];
  }
  let start = -Infinity, end = Infinity;
  for (const [delta, velocity, extent] of [
    [dx, direction.x, a.halfWidth + b.halfWidth + gap],
    [dy, direction.y, a.halfHeight + b.halfHeight + gap],
  ]) {
    if (Math.abs(velocity) < 1e-12) {
      if (Math.abs(delta) >= extent) return;
      continue;
    }
    const first = (-extent - delta) / velocity, last = (extent - delta) / velocity;
    start = Math.max(start, Math.min(first, last));
    end = Math.min(end, Math.max(first, last));
  }
  return start < end ? [start, end] : undefined;
}

/** Wall mounting wins before physical height. Remaining ties retain stable formula-distance order. */
export function resolveEloDisplayCollisions(items: readonly DisplayCollisionItem[], gap = 0): Map<string, DisplayPoint> {
  const result = new Map<string, DisplayPoint>();
  const occupied = items.filter(item => !item.movable).map(item => ({ position: item.position, footprints: item.footprints }));
  const distance = (item: DisplayCollisionItem) => Math.hypot(item.formulaPosition.x - item.origin.x, item.formulaPosition.y - item.origin.y);
  const automatic = items.filter(item => item.movable).sort((a, b) => Number(!!b.wallDocked) - Number(!!a.wallDocked) || (b.z ?? 0) - (a.z ?? 0) || distance(a) - distance(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const item of automatic) {
    // Choose the first higher-priority item actually covering this item.
    const blocker = occupied.find(other => item.footprints.some(footprint => other.footprints.some(obstacle => {
      const interval = overlapInterval(footprint, obstacle, { x: 1, y: 0 }, gap);
      return interval && interval[0] < -1e-10 && interval[1] > 1e-10;
    })));
    const dx = blocker ? item.position.x - blocker.position.x : 0;
    const dy = blocker ? item.position.y - blocker.position.y : 0;
    const length = Math.hypot(dx, dy);
    // Coincident mounting points have no connecting vector. Keep the wall normal
    // there, otherwise use the original radial direction and finally positive X.
    const fallback = item.direction ?? {
      x: item.formulaPosition.x - item.origin.x, y: item.formulaPosition.y - item.origin.y,
    };
    const fallbackLength = Math.hypot(fallback.x, fallback.y);
    const direction = length > 1e-12 ? { x: dx / length, y: dy / length }
      : fallbackLength > 1e-12 ? { x: fallback.x / fallbackLength, y: fallback.y / fallbackLength } : { x: 1, y: 0 };
    const intervals: [number, number][] = [];
    const obstacles = occupied.flatMap(entry => entry.footprints);
    for (const footprint of item.footprints) for (const other of obstacles) {
      const interval = overlapInterval(footprint, other, direction, gap);
      if (interval && interval[1] > 0) intervals.push(interval);
    }
    intervals.sort((a, b) => a[0] - b[0]);
    let travel = 0;
    for (const [start, end] of intervals) {
      // Stop at the first free point, including a boundary shared by two obstacles.
      // The tolerance handles rounding only; it adds no visible spacing.
      if (start >= travel - 1e-10) break;
      if (end > travel + 1e-10) travel = end;
    }
    const shift = { x: direction.x * travel, y: direction.y * travel };
    if (travel > 0) result.set(item.id, { x: item.position.x + shift.x, y: item.position.y + shift.y });
    occupied.push({ position: { x: item.position.x + shift.x, y: item.position.y + shift.y },
      footprints: item.footprints.map(footprint => ({ ...footprint, x: footprint.x + shift.x, y: footprint.y + shift.y })) });
  }
  return result;
}
